import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import api from '../utils/axiosConfig';
import { useAppState } from '../contexts/AppStateContext';

// Import field components
import InputField from "./form-fields/InputField";
import FileField from "./form-fields/FileField";
import CheckBoxField from "./form-fields/CheckBoxField";
import SelectField from "./form-fields/SelectField";
import RadioButtonField from "./form-fields/RadioButtonField";
import MultipleField from "./form-fields/MultipleField";
import TextAreaField from "./form-fields/TextAreaField";
import EmailField from "./form-fields/EmailField";
import FirstNameField from "./form-fields/FirstNameField";
import LastNameField from "./form-fields/LastNameField";
import PhoneField from "./form-fields/PhoneField";
import Preview from "./Preview";

function FormBuilder() {
  const { currentEvent, currentOrganization } = useAppState();

  const [fields, setFields] = useState([]);
  const [activeField, setActiveField] = useState(null);
  const [rtl, setRtl] = useState(false);
  const [submitButtonText, setSubmitButtonText] = useState("Submit");
  const [submitButtonColor, setSubmitButtonColor] = useState("#4f46e5");
  const [submitButtonTextColor, setSubmitButtonTextColor] = useState("#ffffff");

  const [hasFirstNameField, setHasFirstNameField] = useState(false);
  const [hasLastNameField, setHasLastNameField] = useState(false);
  const [hasEmailField, setHasEmailField] = useState(false);
  const [hasPhoneField, setHasPhoneField] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Check and update required fields
  const updateRequiredFields = (fields, override = true) => {
    if (override) {
      setHasFirstNameField(false);
      setHasLastNameField(false);
      setHasEmailField(false);
      setHasPhoneField(false);
    }

    fields.forEach(field => {
      if (field.field === "FirstNameField") {
        setHasFirstNameField(true);
      }
      if (field.field === "LastNameField") {
        setHasLastNameField(true);
      }
      if (field.field === "EmailField") {
        setHasEmailField(true);
      }
      if (field.field === "PhoneField") {
        setHasPhoneField(true);
      }
      if (field.field === "MultipleField") {
        updateRequiredFields(field.nestedFields, false);
      }
    });
  };

  // Load form data when component mounts
  useEffect(() => {
    const getFormData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`/organizations/${currentOrganization.id}/events/${currentEvent.id}/`);
        const formStructure = response.data.form_structure;
        if (formStructure) {
          setFields(formStructure.fields || []);
          setSubmitButtonText(formStructure.button_text || "Submit");
          setSubmitButtonColor(formStructure.button_color || "#4f46e5");
          setSubmitButtonTextColor(formStructure.button_text_color || "#ffffff");
          setRtl(formStructure.rtl || false);
          updateRequiredFields(formStructure.fields || []);
        } 
        setIsLoading(false);

      } catch (error) {
        console.error("Error loading form data:", error);
        setIsLoading(false);
      }
    };

    getFormData();
  }, []);

  // Form settings handlers
  const onSubmitButtonTextChange = (e) => {
    setSubmitButtonText(e.target.value);
  };

  const onSubmitButtonColorChange = (e) => {
    setSubmitButtonColor(e.target.value);
  };

  const onSubmitButtonTextColorChange = (e) => {
    setSubmitButtonTextColor(e.target.value);
  };

  const onRtlChange = (e) => {
    setRtl(e.target.checked);
  };

  // Map field types to components
  const fieldNameMap = {
    "InputField": InputField,
    "EmailField": EmailField,
    "FirstNameField": FirstNameField,
    "LastNameField": LastNameField,
    "PhoneField": PhoneField,
    "FileField": FileField,
    "CheckBoxField": CheckBoxField,
    "SelectField": SelectField,
    "RadioButtonField": RadioButtonField,
    "MultipleField": MultipleField,
    "TextAreaField": TextAreaField,
  };

  // Save form to the server
  const saveForm = async () => {
    if (!hasEmailField || !hasFirstNameField || !hasLastNameField) {
      alert("Email, First Name and Last Name fields are required");
      return;
    }

    try {
      setIsLoading(true);

      // Send form data to server
      await api.put(`/organizations/${currentOrganization.id}/events/${currentEvent.id}/`, {
        form_structure: {
          fields: fields,
          button_text: submitButtonText,
          button_color: submitButtonColor,
          button_text_color: submitButtonTextColor,
          rtl
        }
      });

      setIsLoading(false);
    } catch (error) {
      console.error("Error saving form:", error);
      alert("Error saving form. Please try again.");
      setIsLoading(false);
    }
  };

  // Add a new field to the form
  const addNewField = (fieldType) => {
    const randomId = Math.floor(Math.random() * 1000);
    let field = {};

    switch (fieldType) {
      case 'input':
        field = {
          field: "InputField",
          type: "text",
          name: `input_${randomId}`,
          label: "Input",
          hasRange: false,
          minValue: 0,
          maxValue: 0,
          isRequired: false,
          id: uuidv4(),
        };
        break;
      case 'email':
        field = {
          field: "EmailField",
          type: "email",
          label: "Email",
          name: 'email',
          isRequired: true,
          id: uuidv4(),
        };
        break;
      case 'firstname':
        field = {
          field: "FirstNameField",
          type: "text",
          name: "first_name",
          label: "First Name",
          isRequired: true,
          id: uuidv4(),
        };
        break;
      case 'lastname':
        field = {
          field: "LastNameField",
          type: "text",
          name: "last_name",
          label: "Last Name",
          isRequired: true,
          id: uuidv4(),
        };
        break;
      case 'phone':
        field = {
          field: "PhoneField",
          type: "tel",
          name: "phone",
          label: "Phone",
          isRequired: true,
          id: uuidv4(),
        };
        break;
      case 'file':
        field = {
          field: "FileField",
          type: "file",
          name: `file_${randomId}`,
          label: "File",
          isRequired: false,
          fileExtensions: [],
          id: uuidv4(),
        };
        break;
      case 'checkbox':
        field = {
          field: "CheckBoxField",
          type: "checkbox",
          name: `checkbox_${randomId}`,
          label: "Checkbox",
          isRequired: false,
          id: uuidv4(),
        };
        break;
      case 'select':
        field = {
          field: "SelectField",
          type: "select",
          name: `select_${randomId}`,
          label: "Select",
          isRequired: false,
          allowMultipleValue: false,
          options: [{ id: uuidv4(), value: "option 1" }],
          id: uuidv4(),
        };
        break;
      case 'radio':
        field = {
          field: "RadioButtonField",
          type: "radio",
          name: `radio_${randomId}`,
          label: "Radio",
          isRequired: false,
          choices: [{ id: uuidv4(), value: "choice 1" }],
          id: uuidv4(),
        };
        break;
      case 'multiple2':
        field = {
          field: "MultipleField",
          type: "multiple",
          elementNumber: 2,
          nestedFields: [],
          id: uuidv4(),
        };
        break;
      case 'multiple3':
        field = {
          field: "MultipleField",
          type: "multiple",
          elementNumber: 3,
          nestedFields: [],
          id: uuidv4(),
        };
        break;
      case 'textarea':
        field = {
          field: "TextAreaField",
          type: "textarea",
          name: `textarea_${randomId}`,
          label: "Textarea",
          isRequired: false,
          id: uuidv4(),
        };
        break;
      default:
        return;
    }

    const newFields = [...fields, field];
    setFields(newFields);
    updateRequiredFields(newFields);
  };

  // Update field data
  const onFieldDataChange = (id, data) => {
    const newFields = fields.map(field => {
      if (field.id === id) {
        return { ...field, ...data };
      }
      return field;
    });

    setFields(newFields);
    updateRequiredFields(newFields);
  };

  // Remove a field from the form
  const removeField = (id) => {
    const newFields = fields.filter(field => field.id !== id);
    setFields(newFields);
    updateRequiredFields(newFields);
  };

  // Move a field up or down
  const moveField = (fieldId, direction) => {
    const fieldIndex = fields.findIndex((field) => field.id === fieldId);

    if (
      (direction === "up" && fieldIndex === 0) ||
      (direction === "down" && fieldIndex === fields.length - 1)
    ) {
      return;
    }

    const newFields = [...fields];
    const movedField = newFields.splice(fieldIndex, 1)[0];
    const newIndex = direction === "up" ? fieldIndex - 1 : fieldIndex + 1;
    newFields.splice(newIndex, 0, movedField);

    setFields(newFields);
  };

  return (
    <div className="p-6 bg-gray-50">
      {/* Loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <p className="text-xl font-semibold mb-4">Saving form...</p>
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce delay-75"></div>
              <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce delay-150"></div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Form Builder</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors"
          >
            {previewMode ? 'Builder Mode' : 'Preview Mode'}
          </button>
          <button
            onClick={saveForm}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
          >
            Save Form
          </button>
        </div>
      </div>

      {previewMode ? (
        /* Preview mode */
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Form Preview</h3>
            <p className="text-gray-500 text-sm mb-4">This is how your form will appear to users.</p>
          </div>
          <div className="border rounded-lg p-6 bg-gray-50">
            <Preview
              fields={fields}
              rtl={rtl}
              submitButtonText={submitButtonText}
              submitButtonColor={submitButtonColor}
              submitButtonTextColor={submitButtonTextColor}
            />
          </div>
        </div>
      ) : (
        /* Builder mode */
        <div className="space-y-6">
          {/* Form settings panel */}
          <div className="bg-white rounded-lg shadow-md mb-6 border border-gray-200">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900">Form Settings</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">Submit Button Text</label>
                <input
                  type="text"
                  value={submitButtonText}
                  onChange={onSubmitButtonTextChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">Button Color</label>
                <input
                  type="color"
                  value={submitButtonColor}
                  onChange={onSubmitButtonColorChange}
                  className="w-full h-10 p-1 border border-gray-300 rounded-md shadow-sm"
                />
              </div>
              <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                <input
                  type="color"
                  value={submitButtonTextColor}
                  onChange={onSubmitButtonTextColorChange}
                  className="w-full h-10 p-1 border border-gray-300 rounded-md shadow-sm"
                />
              </div>
              <div className="bg-gray-50 p-4 rounded-md border border-gray-100 flex items-center">
                <input
                  id="rtl-toggle"
                  type="checkbox"
                  checked={rtl}
                  onChange={onRtlChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="rtl-toggle" className="ml-2 block text-sm text-gray-700">
                  Right-to-Left (RTL) Layout
                </label>
              </div>
            </div>
          </div>

          {/* Add field panel */}
          <div className="bg-white rounded-lg shadow-md mb-6 border border-gray-200">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900">Add Field</h3>
            </div>
            <div className="p-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {!hasEmailField && (
                <button
                  onClick={() => addNewField('email')}
                  className="px-4 py-3 bg-indigo-50 rounded-md border border-indigo-100 hover:bg-indigo-100 transition-colors text-sm font-medium text-indigo-700 flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email Field
                </button>
              )}
              {!hasFirstNameField && (
                <button
                  onClick={() => addNewField('firstname')}
                  className="px-4 py-3 bg-indigo-50 rounded-md border border-indigo-100 hover:bg-indigo-100 transition-colors text-sm font-medium text-indigo-700 flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  First Name Field
                </button>
              )}
              {!hasLastNameField && (
                <button
                  onClick={() => addNewField('lastname')}
                  className="px-4 py-3 bg-indigo-50 rounded-md border border-indigo-100 hover:bg-indigo-100 transition-colors text-sm font-medium text-indigo-700 flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Last Name Field
                </button>
              )}
              {!hasPhoneField && (
                <button
                  onClick={() => addNewField('phone')}
                  className="px-4 py-3 bg-indigo-50 rounded-md border border-indigo-100 hover:bg-indigo-100 transition-colors text-sm font-medium text-indigo-700 flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Phone Field
                </button>
              )}
              <button
                onClick={() => addNewField('input')}
                className="px-4 py-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Input Field
              </button>
              <button
                onClick={() => addNewField('textarea')}
                className="px-4 py-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Text Area
              </button>
              <button
                onClick={() => addNewField('select')}
                className="px-4 py-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                </svg>
                Select Field
              </button>
              <button
                onClick={() => addNewField('radio')}
                className="px-4 py-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Radio Buttons
              </button>
              <button
                onClick={() => addNewField('checkbox')}
                className="px-4 py-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Checkbox
              </button>
              <button
                onClick={() => addNewField('file')}
                className="px-4 py-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                File Upload
              </button>
              <button
                onClick={() => addNewField('multiple2')}
                className="px-4 py-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                2-Column Row
              </button>
              <button
                onClick={() => addNewField('multiple3')}
                className="px-4 py-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors text-sm font-medium flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                3-Column Row
              </button>
            </div>
          </div>

          {/* Form fields list */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900">Form Fields</h3>
            </div>
            <div>
              {fields.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500">
                    No fields added yet. Use the buttons above to add form fields.
                  </p>
                </div>
              ) : (
                fields.map((field, index) => {
                  const Field = fieldNameMap[field.field];
                  return (
                    <div
                      key={field.id}
                      className="border-b border-gray-200 last:border-b-0 transition-colors relative"
                    >
                      <div
                        className="p-6 hover:bg-gray-50 transition-all relative rounded-md m-2 border border-gray-600 shadow-sm"
                        onMouseEnter={() => setActiveField(field.id)}
                        onMouseLeave={() => setActiveField(null)}
                      >
                        {/* Field badge */}
                        <div className="absolute -top-3 left-3 py-1 px-3 bg-gray-100 text-gray-600 text-xs rounded-full font-medium inline-block border border-gray-600 shadow-sm z-10">
                          {field.field.replace('Field', '')}
                        </div>

                        {/* Controls */}
                        <div
                          className={`absolute top-2 right-2 z-10 flex bg-white shadow rounded-md border border-gray-100 transition-opacity duration-200 ${activeField === field.id ? 'opacity-100' : 'opacity-0'
                            }`}
                        >
                          {index > 0 && (
                            <button
                              onClick={() => moveField(field.id, "up")}
                              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                              title="Move Up"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                              </svg>
                            </button>
                          )}
                          {index < fields.length - 1 && (
                            <button
                              onClick={() => moveField(field.id, "down")}
                              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                              title="Move Down"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => removeField(field.id)}
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50"
                            title="Remove Field"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        <div className="pt-2">
                          <Field
                            {...field}
                            updateFieldData={onFieldDataChange}
                            hasEmailField={hasEmailField}
                            hasFirstNameField={hasFirstNameField}
                            hasLastNameField={hasLastNameField}
                            hasPhoneField={hasPhoneField}
                            className="bg-white rounded-md p-4"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FormBuilder;